import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

/**
 * The roles catalogue.
 *
 * WHY THIS EXISTS
 * The careers page used to hard-code its role list. That list drifted from the `roles` table it
 * submits against — different titles ("SDE 2 (Backend / Full-Stack)" here vs "Senior Backend /
 * Full-Stack Developer" in the database), and one role that existed only in the database. The
 * listings were switched off rather than reconciled, which left the apply flow guessing a role
 * from the candidate's years of experience.
 *
 * The split now is:
 *   - the DATABASE is authoritative for which roles exist, and for slug / title / domain / kind;
 *   - this file supplies PRESENTATION copy (blurb, responsibilities, headline skills, salary),
 *     keyed by slug, because none of that belongs in an operational table.
 *
 * A role in the database with no copy here still renders, with a neutral description. Copy here
 * for a role that is not active in the database simply never renders. Neither can drift into a
 * broken listing.
 *
 * ⚠️ SALARY IS DELIBERATELY NOT READ FROM THE DATABASE.
 * `roles.salary_band_min/max` holds figures roughly 3.6–4.5× the advertised ranges (e.g. ₹54–81L
 * against an advertised ₹14–22 LPA). Those are evidently not candidate compensation — most likely
 * a Europe-side billing figure. Until that is confirmed, advertising them would be badly wrong,
 * so the candidate-facing salary stays here.
 */

/** Presentation copy, keyed by role slug. */
export const ROLE_COPY = {
  backend: {
    skills: ["Java", "Spring Boot", "Python", "Django/FastAPI", "React", "PostgreSQL"],
    experience: "3–6 years",
    salary: "₹14–22 LPA",
    count: 5,
    desc: "Build and maintain scalable backend services and full-stack features across the product stack. You'll work across APIs, databases, and frontend — writing production-quality code, participating in design discussions, and growing into greater ownership over time.",
    expectations: [
      "Develop and maintain RESTful APIs and microservices following established patterns and best practices",
      "Write efficient queries, contribute to schema design, and flag performance bottlenecks",
      "Work within existing CI/CD pipelines and contribute to deployment improvements",
      "Participate in code reviews and maintain high code quality standards",
      "Collaborate with product and design teams to translate requirements into robust technical solutions",
    ],
  },
  dataeng: {
    skills: ["Python", "SQL", "Spark", "Airflow", "dbt", "Snowflake"],
    experience: "3–5 years",
    salary: "₹12–20 LPA",
    count: 4,
    desc: "Build robust data pipelines and warehouse architectures that power business decisions. Design ETL/ELT workflows, real-time streaming systems, and scalable data infrastructure at enterprise scale.",
    expectations: [
      "Design, build, and maintain ETL/ELT pipelines using Airflow, Spark, or equivalent",
      "Build and optimise data warehouse models using dbt and Snowflake",
      "Monitor pipeline health, troubleshoot failures, and ensure data reliability",
      "Collaborate with analysts and ML engineers on data contracts and schema design",
      "Document data lineage and enforce data quality standards across the platform",
    ],
  },
  aiml: {
    skills: ["Python", "PyTorch", "TensorFlow", "MLOps", "LLMs", "Docker"],
    experience: "3–5 years",
    salary: "₹16–30 LPA",
    count: 3,
    desc: "Build and deploy production machine learning systems — from classical ML models to LLM-powered applications. Ship real AI products that solve real problems, not research prototypes.",
    expectations: [
      "Train, evaluate, and iterate on ML models across supervised, unsupervised, and generative paradigms",
      "Deploy models to production via REST APIs, batch inference pipelines, or streaming systems",
      "Monitor model performance, data drift, and degradation in live environments",
      "Build and integrate LLM-powered features including prompt pipelines, RAG systems, and fine-tuned models",
      "Write clean, reproducible ML code with thorough experiment tracking and documentation",
    ],
  },
  bi: {
    skills: ["SQL", "Power BI", "Tableau", "Data Modeling", "Python", "Stakeholder Reporting"],
    experience: "3–5 years",
    salary: "₹12–20 LPA",
    count: 3,
    desc: "Turn raw data into actionable decisions — building dashboards, reports, and analyses that business stakeholders rely on daily to plan, measure, and improve.",
    expectations: [
      "Write complex SQL queries and build reusable, well-documented data models",
      "Design and maintain dashboards and reports in Power BI or Tableau",
      "Translate ambiguous business questions into clear analytical frameworks and hypotheses",
      "Present findings and recommendations clearly to non-technical stakeholders",
      "Define KPIs, own reporting for key business functions, and drive data-informed decision-making",
    ],
  },
  pm: {
    skills: ["Agile", "Scrum", "Jira", "Stakeholder Management", "Risk Management", "SDLC"],
    experience: "5–10 years",
    salary: "₹18–28 LPA",
    count: 2,
    desc: "Run delivery for international engineering programmes — owning sprint cadence, release planning, and the client relationship, so teams can focus on building.",
    expectations: [
      "Own sprint planning, backlog grooming, and release cadence across one or more delivery teams",
      "Track risks and dependencies, and escalate early with a proposed path forward",
      "Act as the primary point of contact for client stakeholders in European timezones",
      "Report progress honestly — including when a date is going to move, before it moves",
      "Improve delivery process based on what the team actually experiences, not what the framework prescribes",
    ],
  },
};

const FALLBACK_COPY = {
  skills: [],
  experience: "",
  salary: "",
  count: 1,
  desc: "We're hiring for this role. Apply and our talent team will walk you through the details.",
  expectations: [],
};

function decorate(row) {
  const copy = ROLE_COPY[row.slug] || FALLBACK_COPY;
  return {
    // `id` is what the existing components key and compare on.
    id: row.slug,
    slug: row.slug,
    title: row.title,
    domain: row.domain,
    // `kind` may be absent until the role-kind migration is applied; treat that as an opening.
    kind: row.kind || "opening",
    ...copy,
  };
}

let cachedPromise = null;

/** Fetch active roles once per page load and share the result across components. */
function loadRoles() {
  if (cachedPromise) return cachedPromise;
  cachedPromise = (async () => {
    if (!supabase) throw new Error("Roles are unavailable right now.");
    // `select('*')` rather than a column list so this keeps working before the
    // role-kind migration is applied — a named column that does not exist yet is an error.
    const { data, error } = await supabase
      .from("roles")
      .select("*")
      .eq("status", "active")
      .order("title");
    if (error) throw new Error(error.message);
    return (data || []).map(decorate);
  })();
  return cachedPromise;
}

/**
 * @returns {{ openings: object[], pools: object[], loading: boolean, error: string }}
 */
export function useRolesCatalog() {
  const [state, setState] = useState({ openings: [], pools: [], loading: true, error: "" });

  useEffect(() => {
    let active = true;
    loadRoles()
      .then((roles) => {
        if (!active) return;
        setState({
          openings: roles.filter((r) => r.kind === "opening"),
          pools: roles.filter((r) => r.kind === "talent_pool"),
          loading: false,
          error: "",
        });
      })
      .catch((e) => {
        if (!active) return;
        // A failed fetch must not take the page down — the general application path
        // still works, and the listings simply do not render.
        setState({ openings: [], pools: [], loading: false, error: e.message || "Could not load roles." });
      });
    return () => {
      active = false;
    };
  }, []);

  return state;
}

/**
 * Which talent pool a general application belongs to.
 *
 * Returns the pool matching the chosen sector, falling back to any pool so a candidate is never
 * turned away because their sector has no bucket. Returns null only if no pools exist at all,
 * which means the role-kind migration has not been applied yet.
 */
export function poolForSector(pools, sector) {
  if (!pools || pools.length === 0) return null;
  return pools.find((p) => p.domain === sector) || pools[0];
}
